/**
 * This is matching logic from the backend calulations for clearing sale billing documents.
 * It will soon be replaced by upcoming clearning sale billing run work.
 */
import {
  BuyerPaymentSummaryData,
  BuyerPaymentSummaryItem,
} from "constants/payments";

type Lookup<T> = Record<string, T>;

/**
 * Calculate GST on an amount (10%)
 */
export function calculateGst(amountCents: number): number {
  return Math.round(amountCents * 0.1);
}

/**
 * Calculate buyers premium for a sale lot (returns cents)
 */
export function calculateBuyersPremium(
  saleLot: SaleLot,
  salePriceCents: number,
  deploymentSale: DeploymentSale | null,
): number {
  const defaultBuyersPremium = deploymentSale?.default_buyers_premium || 0;
  const effectiveBuyersPremiumDecimal =
    saleLot.buyersPremium != null
      ? saleLot.buyersPremium
      : defaultBuyersPremium;

  // Match backend calculate_percentage: convert to dollars, multiply by percentage,
  // round to nearest cent
  const salePriceDollars = salePriceCents / 100;
  const premiumDollars = salePriceDollars * effectiveBuyersPremiumDecimal;
  const premiumCents = Math.round(premiumDollars * 100);
  return premiumCents;
}

export interface BuyerInvoiceBreakdown {
  salePriceCents: number;
  salePriceGstCents: number;
  buyersPremiumCents: number;
  buyersPremiumGstCents: number;
  totalCents: number;
}

export interface BuyerPaymentSummaryLotInput {
  saleLot: SaleLot;
  lotTitle: string;
  chargeSalePriceGst: boolean;
}

/**
 * Calculate a breakdown of buyer invoice components
 * Returns the individual components (sale price, GST, fees) and the total
 */
const EMPTY_BREAKDOWN: BuyerInvoiceBreakdown = {
  salePriceCents: 0,
  salePriceGstCents: 0,
  buyersPremiumCents: 0,
  buyersPremiumGstCents: 0,
  totalCents: 0,
};

export function calculateBuyerInvoiceBreakdown(
  saleLot: SaleLot,
  chargeSalePriceGst: boolean,
  deploymentSalesBySaleLotIdLookup: Lookup<DeploymentSale>,
  chargeBuyersPremiumGst: boolean,
): BuyerInvoiceBreakdown {
  if (!saleLot?.buyer_id) {
    return EMPTY_BREAKDOWN;
  }

  const salePriceCents = saleLot.total_price_cents || 0;
  const deploymentSale = deploymentSalesBySaleLotIdLookup[saleLot.id] || null;

  // Sale price GST (10% if vendor GST applicable)
  const salePriceGstCents =
    chargeSalePriceGst === true ? calculateGst(salePriceCents) : 0;

  // Buyers premium
  const buyersPremiumCents = calculateBuyersPremium(
    saleLot,
    salePriceCents,
    deploymentSale,
  );

  // Buyers premium GST (10% if agency is GST registered)
  const buyersPremiumGstCents =
    chargeBuyersPremiumGst === true ? calculateGst(buyersPremiumCents) : 0;

  const totalCents =
    salePriceCents +
    salePriceGstCents +
    buyersPremiumCents +
    buyersPremiumGstCents;

  return {
    salePriceCents,
    salePriceGstCents,
    buyersPremiumCents,
    buyersPremiumGstCents,
    totalCents,
  };
}

export const calculateBuyerInvoiceTotal = (
  saleLot: SaleLot,
  chargeSalePriceGst: boolean,
  deploymentSalesBySaleLotIdLookup: Lookup<DeploymentSale>,
  chargeBuyersPremiumGst: boolean,
): number => {
  const breakdown = calculateBuyerInvoiceBreakdown(
    saleLot,
    chargeSalePriceGst,
    deploymentSalesBySaleLotIdLookup,
    chargeBuyersPremiumGst,
  );
  return breakdown.totalCents;
};

/**
 * A consistent display title for a clearing sale lot.
 */
export function getClearingSaleLotTitle(saleLot: SaleLot): string {
  return (
    saleLot?.clearingSaleAttributes?.title ||
    `Lot ${saleLot.lot_number || saleLot.id}`
  );
}

/**
 * Sum payments originating from a business (buyer) in cents.
 */
export function calculatePaymentsTotalCentsFromBusiness(
  payments: Record<string, Payment>,
  fromBusinessId: string,
): number {
  return Object.values(payments).reduce((sum: number, payment: Payment) => {
    if (!payment || payment.fromBusinessId !== fromBusinessId) {
      return sum;
    }
    return sum + (Number(payment.totalAmountCents) || 0);
  }, 0);
}

/**
 * Calculate Buyer Payment Summary directly from sale lots + GST flags.
 * This keeps selectors "wiring-only" and makes the breakdown step an internal implementation detail.
 */
export function calculateBuyerBalanceSummaryDataFromLots(
  lots: BuyerPaymentSummaryLotInput[],
  deploymentSalesBySaleLotIdLookup: Lookup<DeploymentSale>,
  chargeBuyersPremiumGst: boolean,
  paymentsTotalCents: number,
): BuyerPaymentSummaryData {
  const items: BuyerPaymentSummaryItem[] = [];
  let grandTotalCents = 0;
  let feesCents = 0;
  let taxCents = 0;

  lots.forEach(({ saleLot, lotTitle, chargeSalePriceGst }) => {
    const breakdown = calculateBuyerInvoiceBreakdown(
      saleLot,
      chargeSalePriceGst,
      deploymentSalesBySaleLotIdLookup,
      chargeBuyersPremiumGst,
    );

    feesCents += breakdown.buyersPremiumCents;
    taxCents += breakdown.salePriceGstCents + breakdown.buyersPremiumGstCents;
    grandTotalCents += breakdown.totalCents;

    items.push({
      lotId: saleLot.id,
      lotTitle,
      quantity: saleLot.quantity || 0,
      amountInclCents: breakdown.totalCents,
    });
  });

  const amountExclCents = grandTotalCents - taxCents - feesCents;
  const dueCents = grandTotalCents - paymentsTotalCents;

  return {
    items,
    totals: {
      amountExclCents,
      feesCents,
      taxCents,
      dueCents,
    },
  };
}
